import { useEffect, useRef, useState } from 'react';
import { Form, Formik } from 'formik';
import * as Yup from 'yup';
import { AxiosError } from 'axios';
import { Button } from '@/components/ui/button';
import { FormikInput } from '@/components/ui/input';
import { CircleCheck, Clock, CreditCard, MailCheck, ShieldCheck } from 'lucide-react';
import { cadastrar } from '@/services/CadastroService';
import { mensagemDeErro } from '@/utils/erro-api';
import { Ciclo, Plano, lerPlano } from '@/utils/planos';
import { SeletorPlano } from '@/components/planos/SeletorPlano';
import { cn } from '@/utils/cn';
import { ReactComponent as Logo } from '@/assets/img/logo-transparent.svg';

declare global {
  interface Window {
    turnstile?: {
      render: (el: HTMLElement, opts: { sitekey: string; callback: (token: string) => void; 'expired-callback'?: () => void; 'error-callback'?: () => void }) => string;
      reset: (id?: string) => void;
    };
    fbq?: Fbq;
    _fbq?: Fbq;
  }
}

type Fbq = ((...args: unknown[]) => void) & { callMethod?: (...args: unknown[]) => void; queue: unknown[][]; push?: Fbq; loaded: boolean; version: string };

const SITEKEY = process.env.REACT_APP_TURNSTILE_SITEKEY || '1x00000000000000000000AA';
const SCRIPT = 'https://challenges.cloudflare.com/turnstile/v0/api.js?render=explicit';

// Carrega o script do Turnstile uma única vez
let scriptPronto: Promise<void> | undefined;
const carregarTurnstile = () => {
  scriptPronto ??= new Promise<void>((resolve, reject) => {
    const s = document.createElement('script');
    s.src = SCRIPT;
    s.async = true;
    s.onload = () => resolve();
    s.onerror = () => { scriptPronto = undefined; reject(new Error('turnstile')); };
    document.head.appendChild(s);
  });
  return scriptPronto;
};

const APP = 'https://app.vigento.com.br';
const SITE = 'https://vigento.com.br';

// Pixel da Meta (mede os anúncios): só em produção e só depois do consentimento, porque grava cookie de marketing
const PIXEL_META = window.location.hostname === 'app.vigento.com.br' ? '3955049511296123' : undefined;
const CONSENTIMENTO = 'consentimento-pixel-meta';

const lerConsentimento = () => { try { return localStorage.getItem(CONSENTIMENTO); } catch { return null; } };
const gravarConsentimento = (valor: 'sim' | 'nao') => {
  try { localStorage.setItem(CONSENTIMENTO, valor); } catch { /* sem storage: pergunta de novo na próxima visita */ }
};

// Equivalente ao snippet oficial: enfileira as chamadas até o fbevents.js carregar
const iniciarPixel = () => {
  if (!PIXEL_META || window.fbq) return;
  const fbq: Fbq = Object.assign(
    (...args: unknown[]) => { if (fbq.callMethod) fbq.callMethod(...args); else fbq.queue.push(args); },
    { queue: [] as unknown[][], loaded: true, version: '2.0' });
  fbq.push = fbq;
  window.fbq = window._fbq = fbq;
  const s = document.createElement('script');
  s.async = true;
  s.src = 'https://connect.facebook.net/en_US/fbevents.js';
  document.head.appendChild(s);
  fbq('init', PIXEL_META);
  fbq('track', 'PageView');
};

const AvisoPixel = () => {
  const [resposta, setResposta] = useState(lerConsentimento);
  useEffect(() => { if (resposta === 'sim') iniciarPixel(); }, [resposta]);
  if (!PIXEL_META || resposta) return null;
  const responder = (valor: 'sim' | 'nao') => { gravarConsentimento(valor); setResposta(valor); };
  return (
    <div role="region" aria-label="Aviso de cookies" className="fixed inset-x-0 bottom-0 z-50 border-t bg-white px-5 py-4 shadow-[0_-8px_24px_-12px_rgba(0,0,0,.25)]">
      <div className="mx-auto flex max-w-4xl flex-col gap-3 text-sm text-[#10232A] sm:flex-row sm:items-center">
        <p className="flex-1">
          Usamos o Pixel da Meta para medir os resultados dos nossos anúncios. Ele só é ativado se você aceitar.{' '}
          <a className="underline" href={`${SITE}/privacidade/`} target="_blank" rel="noopener noreferrer">Saiba mais</a>
        </p>
        <div className="flex gap-2">
          <Button variant="outline" className="flex-1 sm:flex-none" onClick={() => responder('nao')}>Recusar</Button>
          <Button className="flex-1 bg-[#0F4C5C] text-white hover:bg-[#0A3540] sm:flex-none" onClick={() => responder('sim')}>Aceitar</Button>
        </div>
      </div>
    </div>
  );
};

const BENEFICIOS = [
  'Alerta semanal das renovações que vencem nos próximos 30 dias',
  'Seguros, endossos, sinistros e comissões em um só lugar',
  'Aviso de renovação para o cliente pelo WhatsApp',
  'Equipe com permissões por usuário e auditoria',
];

const Marca = ({ clara = false }: { clara?: boolean }) => (
  <a href={SITE} className={cn('inline-flex items-center gap-2.5 text-xl font-extrabold tracking-tight no-underline', clara ? 'text-white' : 'text-[#0F4C5C]')}>
    <span className={cn('flex h-9 w-9 items-center justify-center rounded-[10px] p-1', clara ? 'bg-white' : 'bg-[#0F4C5C]')}>
      <Logo aria-hidden className={cn('h-full w-full', clara ? 'fill-[#0F4C5C]' : 'fill-white')} />
    </span>
    Vigento
  </a>
);

// Coluna da esquerda (no celular, só o topo): proposta de valor na identidade do site
const Apresentacao = () => (
  <aside className="bg-[radial-gradient(900px_500px_at_90%_-10%,rgba(43,182,115,.30),transparent_60%),linear-gradient(180deg,#0A3540,#0F4C5C)] px-6 py-8 text-white lg:sticky lg:top-0 lg:flex lg:h-screen lg:w-[44%] lg:flex-col lg:justify-between lg:gap-10 lg:overflow-y-auto lg:px-14 lg:py-12">
    <Marca clara />
    <div className="mt-8 lg:mt-0">
      <h1 className="[text-wrap:balance] text-2xl font-bold leading-tight tracking-tight sm:text-3xl xl:text-[2.1rem]">
        Nenhuma renovação esquecida.<br /> Teste grátis por 14 dias.
      </h1>
      <p className="mt-3 max-w-md text-white/75 lg:text-lg">
        Apólices, segurados, renovações, sinistros e comissões da sua corretora em um só lugar.
      </p>
      <ul className="mt-8 hidden space-y-3.5 lg:block">
        {BENEFICIOS.map(b => (
          <li key={b} className="flex items-start gap-3 text-white/90">
            <CircleCheck aria-hidden className="mt-0.5 h-5 w-5 shrink-0 text-[#2BB673]" />
            {b}
          </li>
        ))}
      </ul>
    </div>
    <ul className="mt-6 flex flex-wrap gap-x-5 gap-y-2 text-sm text-white/70 lg:mt-0 lg:flex-col">
      <li className="flex items-center gap-1.5"><CreditCard aria-hidden className="h-4 w-4" /> Sem cartão de crédito</li>
      <li className="flex items-center gap-1.5"><Clock aria-hidden className="h-4 w-4" /> Pronto em 2 minutos</li>
      <li className="flex items-center gap-1.5"><ShieldCheck aria-hidden className="h-4 w-4" /> Dados protegidos (LGPD)</li>
    </ul>
  </aside>
);

const Pagina = ({ children }: { children: React.ReactNode }) => (
  <div className="min-h-screen bg-white text-[#10232A] lg:flex">
    <Apresentacao />
    <main className="flex flex-1 flex-col px-5 py-8 sm:px-8 lg:px-14 lg:py-12">
      <div className="mx-auto flex w-full max-w-xl flex-1 flex-col">
        <p className="mb-8 text-right text-sm text-[#5B6B72]">
          Já tem conta? <a className="font-semibold text-[#0F4C5C] hover:underline" href={APP}>Entrar</a>
        </p>
        <div className="flex-1">{children}</div>
        <p className="mt-10 text-center text-xs text-[#5B6B72]">
          © {new Date().getFullYear()} Vigento · <a className="hover:underline" href={`${SITE}/termos/`} target="_blank" rel="noopener noreferrer">Termos de Uso</a>
          {' · '}<a className="hover:underline" href={`${SITE}/privacidade/`} target="_blank" rel="noopener noreferrer">Privacidade</a>
        </p>
      </div>
    </main>
    <AvisoPixel />
  </div>
);

const schema = Yup.object({
  nome: Yup.string().trim().required('Informe seu nome').matches(/\S\s+\S/, 'Informe nome e sobrenome'),
  email: Yup.string().trim().email('E-mail inválido').required('Informe seu e-mail'),
  celular: Yup.string().required('Informe seu celular')
    .test('celular', 'Celular inválido', v => (v || '').replace(/\D/g, '').length >= 10),
  corretora: Yup.string().trim().required('Informe o nome da corretora'),
});

const CadastroPage = () => {
  const inicial = lerPlano(window.location.search);
  const [plano, setPlano] = useState<Plano>(inicial.plano);
  const [ciclo, setCiclo] = useState<Ciclo>(inicial.ciclo);
  const [token, setToken] = useState('');
  const [erroTurnstile, setErroTurnstile] = useState(false);
  const [erro, setErro] = useState<string>();
  const [jaTemConta, setJaTemConta] = useState(false);
  const [emailEnviado, setEmailEnviado] = useState<string>();
  const widget = useRef<HTMLDivElement>(null);
  const widgetId = useRef<string>();

  useEffect(() => {
    let ativo = true;
    carregarTurnstile()
      .then(() => {
        if (ativo && widget.current && !widgetId.current && window.turnstile) {
          widgetId.current = window.turnstile.render(widget.current, {
            sitekey: SITEKEY,
            callback: setToken,
            'expired-callback': () => setToken(''),
            'error-callback': () => setToken(''),
          });
        }
      })
      .catch(() => ativo && setErroTurnstile(true));
    return () => { ativo = false; };
  }, []);

  if (emailEnviado) {
    return (
      <Pagina>
        <div className="pt-6 text-center sm:pt-16">
          <span className="mx-auto flex h-14 w-14 items-center justify-center rounded-full bg-[#E7F7EF]">
            <MailCheck aria-hidden className="h-7 w-7 text-[#1f8f57]" />
          </span>
          <h2 className="mt-5 text-2xl font-bold tracking-tight">Confira seu e-mail</h2>
          <p className="mt-2 text-[#5B6B72]">
            Enviamos para <strong className="text-[#10232A]">{emailEnviado}</strong> o link para criar sua senha.
          </p>
          <ol className="mx-auto mt-8 max-w-sm space-y-3 text-left text-sm">
            {['Abra o e-mail do Vigento (veja também o spam)', 'Clique no link e crie sua senha', 'Pronto: sua corretora já está esperando por você'].map((passo, i) => (
              <li key={passo} className="flex items-center gap-3">
                <span className="flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-[#0F4C5C] text-xs font-semibold text-white">{i + 1}</span>
                {passo}
              </li>
            ))}
          </ol>
        </div>
      </Pagina>
    );
  }

  return (
    <Pagina>
      <h2 className="text-2xl font-bold tracking-tight">Crie sua conta</h2>
      <p className="mt-1 text-[#5B6B72]">14 dias grátis em qualquer plano. Você só paga se continuar.</p>

      <div className="mt-8">
        <SeletorPlano plano={plano} ciclo={ciclo} onChange={(p, c) => { setPlano(p); setCiclo(c); }} />
      </div>

      <Formik
        initialValues={{ nome: '', email: '', celular: '', corretora: '' }}
        validationSchema={schema}
        onSubmit={async (valores, { setSubmitting }) => {
          setErro(undefined);
          setJaTemConta(false);
          try {
            await cadastrar({ ...valores, nome: valores.nome.trim(), email: valores.email.trim(), corretora: valores.corretora.trim(), plano, ciclo, turnstile: token });
            setEmailEnviado(valores.email.trim());
            window.fbq?.('track', 'CompleteRegistration');
          } catch (e) {
            const err = e as AxiosError<any>;
            setJaTemConta(err.response?.status === 409);
            setErro(mensagemDeErro(err));
            // O token do Turnstile vale uma só vez
            window.turnstile?.reset(widgetId.current);
            setToken('');
          } finally {
            setSubmitting(false);
          }
        }}
      >
        {({ isSubmitting }) => (
          <Form className="mt-8 space-y-4">
            <div className="grid gap-4 sm:grid-cols-2">
              <div><FormikInput name="nome" label="Nome e sobrenome" autoComplete="name" placeholder="Maria Souza" className="h-11" /></div>
              <div><FormikInput name="celular" label="Celular (WhatsApp)" type="tel" inputMode="tel" autoComplete="tel-national" placeholder="(44) 99999-0000" maxLength={20} className="h-11" /></div>
            </div>
            <div><FormikInput name="email" label="E-mail de acesso" type="email" autoComplete="email" placeholder="maria@corretora.com.br" className="h-11" /></div>
            <div><FormikInput name="corretora" label="Nome da corretora" autoComplete="organization" placeholder="Souza Corretora de Seguros" className="h-11" /></div>

            <div ref={widget} className="flex justify-center pt-1" />
            {erroTurnstile && <p role="alert" className="text-sm text-destructive">Não foi possível carregar a verificação de segurança. Recarregue a página.</p>}

            {erro && (
              <p role="alert" className="rounded-lg bg-red-50 px-4 py-3 text-sm font-medium text-red-700 whitespace-pre-line">
                {erro}{' '}
                {jaTemConta && <a className="underline" href={APP}>Entrar</a>}
              </p>
            )}

            <Button type="submit" disabled={!token || isSubmitting}
              className="h-12 w-full rounded-full bg-[#2BB673] text-base font-semibold text-white shadow-[0_8px_24px_-8px_rgba(43,182,115,.6)] hover:bg-[#24a465] disabled:shadow-none">
              {isSubmitting ? 'Criando sua conta...' : 'Começar teste grátis'}
            </Button>
            <p className="text-center text-xs text-[#5B6B72]">
              Ao continuar, você concorda com os <a className="underline" href={`${SITE}/termos/`} target="_blank" rel="noopener noreferrer">Termos de Uso</a> e
              a <a className="underline" href={`${SITE}/privacidade/`} target="_blank" rel="noopener noreferrer">Política de Privacidade</a>.
            </p>
          </Form>
        )}
      </Formik>
    </Pagina>
  );
};

export default CadastroPage;
